import * as lang from "lang";

import { BUSINESS_ERROR } from "../../model/application/Api";

export interface IErrorDetailsObject {
  table?: string;
  constraint?: string;
  detail?: string;
  line?: string;
  [key: string]: any;
}

export type TErrorDetails = IErrorDetailsObject | string;

class CustomBusinessError extends Error {
  status;
  errorStatus;
  name;
  details;
  isBusinessError = true;
  response;
  // Raw backend error_message (already translated server-side), without the
  // technical prefix baked into `message`. Used as the notification headline.
  serverMessage;

  constructor(
    message: string,
    errorStatus: string,
    name: string,
    details?: TErrorDetails,
    serverMessage?: string,
  ) {
    super(message);
    this.status = 200;
    this.errorStatus = errorStatus;
    this.name = name ? name : "InternalError";
    this.serverMessage = serverMessage;
    if (details) this.details = details;
    this.response = {
      data: {
        data: {
          error: {
            message: details,
          },
        },
      },
    };
  }
}

export class PartialFailureBusinessError extends CustomBusinessError {
  constructor(message: string, details?: TErrorDetails) {
    super(
      `Partial error: ${message}`,
      BUSINESS_ERROR.PARTIAL_SUCCESS,
      "PartialFailureError",
      details,
      message,
    );
  }
}

export class ValidationBusinessError extends CustomBusinessError {
  constructor(message: string, details?: TErrorDetails) {
    super(
      `Validation: ${message}`,
      BUSINESS_ERROR.VALIDATION_ERROR,
      "ValidationError",
      details,
      message,
    );
  }
}

export class NotFoundBusinessError extends CustomBusinessError {
  constructor(message: string, details?: TErrorDetails) {
    super(
      `Not found error: ${message}`,
      BUSINESS_ERROR.NOT_FOUND,
      "NotFoundError",
      details,
      message,
    );
  }
}

export class NotAllowedBusinessError extends CustomBusinessError {
  constructor(message: string, details?: TErrorDetails) {
    super(
      `Not allowed error: ${message}`,
      BUSINESS_ERROR.NOT_ALLOWED,
      "NotAllowedError",
      details,
      message,
    );
  }
}

export class AlreadyExistsBusinessError extends CustomBusinessError {
  constructor(message: string, details?: TErrorDetails) {
    super(
      `Already exists error: ${message}`,
      BUSINESS_ERROR.ALREADY_EXISTS,
      "AlreadyExistsError",
      details,
      message,
    );
  }
}

export class RunInBackgroundBusinessError extends CustomBusinessError {
  constructor(details?: TErrorDetails) {
    super(
      `Run in background`,
      BUSINESS_ERROR.RUN_IN_BACKGROUND,
      "RunInBackgroundError",
      details,
    );
  }
}
export class NotUpToDateBusinessError extends CustomBusinessError {
  constructor(message: string, details?: TErrorDetails) {
    super(
      `Not up to date error: ${message}`,
      BUSINESS_ERROR.NOT_UP_TO_DATE,
      "NotUpToDateError",
      details,
      message,
    );
  }
}

export class AuthenticationBusinessError extends CustomBusinessError {
  constructor(message: string, details?: TErrorDetails) {
    super(
      `Authentication error: ${message}`,
      BUSINESS_ERROR.AUTHENTICATION,
      "AuthenticationError",
      details,
      message,
    );
  }
}

export class QuotaExceededBusinessError extends CustomBusinessError {
  constructor(message?: string, details?: TErrorDetails) {
    super(
      message ?? `Quota exceeded. Try later.`,
      BUSINESS_ERROR.QUOTA_EXCEEDED,
      "QuotaExceededError",
      details,
    );
  }
}

export class PayloadTooLargeBusinessError extends CustomBusinessError {
  constructor(details?: TErrorDetails) {
    super(
      `Payload too large. Try to restrict your query.`,
      BUSINESS_ERROR.PAYLOAD_TOO_LARGE,
      "PayloadTooLargeBusinessError",
      details,
    );
  }
}

export class TimeOutBusinessError extends CustomBusinessError {
  constructor(message: string, details?: TErrorDetails) {
    super(
      `Timeout error: ${message}`,
      BUSINESS_ERROR.TIMEOUT,
      "TimeOutBusinessError",
      details,
      message,
    );
  }
}

export class SQLBusinessError extends CustomBusinessError {
  constructor(message: string, details?: TErrorDetails) {
    super(
      `SQL error: ${message}`,
      BUSINESS_ERROR.SQL_ERROR,
      "SqlBusinessError",
      details,
      message,
    );
  }
}

export class FreeTrialLimitReachedError extends CustomBusinessError {
  constructor(message: string, details?: TErrorDetails) {
    super(
      lang["en"].notifications.errorNotifications.freeTrialReachedError,
      BUSINESS_ERROR.FREE_TRIAL_LIMIT_REACHED,
      "FreeTrialError",
      details,
    );
  }
}

export class TimeoutError extends CustomBusinessError {
  constructor(message: string, details?: TErrorDetails) {
    super(
      lang["en"].notifications.errorNotifications.timeoutError,
      BUSINESS_ERROR.TIMEOUT,
      "TimeoutError",
      details,
    );
  }
}

// Cannot import from src/__mocks__ inside app code, so we do it the other way around
export class MockAxiosError extends Error {
  constructor(message: string) {
    super(message);
  }
}
